/**
 * Whole days away.
 *
 * *** STORED PER DAY, SHOWN AS A RANGE ***
 * The server keeps one row per blocked date and groups them for display,
 * which is why a vacation has no id and is removed by its start and end
 * dates rather than by one.
 */

import { useState } from 'react'
import { Card } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { useAddVacation, useRemoveVacation } from '@/lib/queries'
import { formatDate } from '@/lib/format'
import type { Vacation } from '@/types/api'
import {
  AffectedBookingsDialog,
  type ProposedRange,
} from './AffectedBookingsDialog'
import styles from './AvailabilityPage.module.css'

function todayIso(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function TimeOffSection({
  enabled,
  vacations,
  loading,
}: {
  enabled: boolean
  vacations: Vacation[] | null
  loading: boolean
}) {
  const toast = useToast()
  const add = useAddVacation()
  const remove = useRemoveVacation()

  const [start, setStart] = useState(todayIso())
  const [end, setEnd] = useState(todayIso())
  const [reason, setReason] = useState('')
  const [pending, setPending] = useState<ProposedRange | null>(null)

  if (loading) return <p className={styles.muted}>Loading your time off…</p>

  if (!enabled) {
    return (
      <Card>
        <p className={styles.muted}>
          Your institute does not use time off. Ask them to block days for
          you if you are away.
        </p>
      </Card>
    )
  }

  const submit = async () => {
    try {
      await add.mutateAsync({
        start_date: start,
        end_date: end,
        reason: reason.trim() || undefined,
      })
      toast.success('Time off added.')
      setReason('')
      setPending(null)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not add that time off.',
      )
    }
  }

  const drop = async (v: Vacation) => {
    try {
      await remove.mutateAsync({
        start_date: v.start_date,
        end_date: v.end_date,
      })
      toast.success('Time off removed.')
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not remove that.',
      )
    }
  }

  return (
    <>
      <Card>
        <h2 className={styles.sectionTitle}>Book time off</h2>
        <p className={styles.sectionNote}>
          Whole days when you are not available at all.
        </p>

        <div className={styles.offRow}>
          <label className={styles.offField}>
            <span className={styles.fieldLabel}>From</span>
            <input
              type="date"
              className={styles.select}
              value={start}
              min={todayIso()}
              onChange={(e) => {
                setStart(e.target.value)
                // A range that ends before it starts is never what was
                // meant; carry the end along rather than rejecting it.
                if (e.target.value > end) setEnd(e.target.value)
              }}
            />
          </label>

          <label className={styles.offField}>
            <span className={styles.fieldLabel}>To</span>
            <input
              type="date"
              className={styles.select}
              value={end}
              min={start}
              onChange={(e) => setEnd(e.target.value)}
            />
          </label>

          <label className={styles.offField}>
            <span className={styles.fieldLabel}>Reason (optional)</span>
            <input
              className={styles.labelInput}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Holiday"
              maxLength={200}
            />
          </label>

          <Button
            variant="secondary"
            onClick={() =>
              /*
                Whole days, so no times — the server treats a range with
                no times as a full-day block.
              */
              setPending({ start_date: start, end_date: end })
            }
          >
            Add
          </Button>
        </div>
      </Card>

      <Card>
        <h2 className={styles.sectionTitle}>Coming up</h2>

        {vacations && vacations.length === 0 && (
          <p className={styles.muted}>No time off booked.</p>
        )}

        {vacations && vacations.length > 0 && (
          <ul className={styles.oneOffList}>
            {vacations.map((v) => (
              <li
                key={`${v.start_date}:${v.end_date}`}
                className={styles.oneOffRow}
              >
                <span>
                  {v.start_date === v.end_date
                    ? formatDate(v.start_date)
                    : `${formatDate(v.start_date)} – ${formatDate(v.end_date)}`}
                  {' · '}
                  {v.days} day{v.days === 1 ? '' : 's'}
                  {v.reason ? ` · ${v.reason}` : ''}
                </span>
                <button
                  type="button"
                  className={styles.removeX}
                  onClick={() => drop(v)}
                  aria-label="Remove this time off"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {pending && (
        <AffectedBookingsDialog
          range={pending}
          what="time off"
          saving={add.isPending}
          onConfirm={submit}
          onClose={() => setPending(null)}
        />
      )}
    </>
  )
}
